'use client';
import { type ReactNode } from 'react';

export type AlertVariant = 'success' | 'warning' | 'danger' | 'info' | 'default';

export interface AlertProps {
  variant?: AlertVariant;
  title?: ReactNode;
  children?: ReactNode; // body text
  className?: string;
}

// No side stripe (banned). The pale status tint carries meaning and a leading
// monospace glyph signals the variant, per DESIGN.md §Alerts. Glyphs match Toast.
const VARIANT_SURFACE: Record<AlertVariant, string> = {
  success: 'bg-success-pale',
  warning: 'bg-warning-pale',
  danger: 'bg-danger-pale',
  info: 'bg-info-pale',
  default: 'bg-surface-deep',
};

const VARIANT_ICON: Record<AlertVariant, { glyph: string; color: string } | null> = {
  success: { glyph: '✓', color: 'text-success-text' },
  warning: { glyph: '⚠', color: 'text-warning-text' },
  danger: { glyph: '✕', color: 'text-danger-text' },
  info: { glyph: 'i', color: 'text-info-text' },
  default: null,
};

export function Alert({ variant = 'default', title, children, className }: AlertProps) {
  const isDanger = variant === 'danger';
  const icon = VARIANT_ICON[variant];

  return (
    <div
      role={isDanger ? 'alert' : 'status'}
      aria-live={isDanger ? 'assertive' : 'polite'}
      className={[
        'flex items-start gap-2 rounded-control px-4 py-3',
        VARIANT_SURFACE[variant],
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {/* Icon column — always reserve 1ch for alignment across variants. */}
      <span
        aria-hidden="true"
        className={[
          'flex-shrink-0 w-[1ch] font-mono text-caption leading-5 text-center',
          icon ? icon.color : 'text-muted',
        ].join(' ')}
      >
        {icon ? icon.glyph : ''}
      </span>
      <div className="flex-1 min-w-0 space-y-0.5">
        {title && <p className="text-body-sm-emphasis text-primary">{title}</p>}
        {/* A <div>, NOT a <p>: an Alert body legitimately carries block content
            (a suggestion list, a row of clarify chips) alongside its text. A
            paragraph made that invalid markup, and the workaround -- moving the
            block content out of the Alert entirely -- pushed it outside the pale
            status background, visually orphaning it from the message it belongs
            to (#10216). DESIGN.md §Alerts pins the body treatment to 0.75rem /
            1.5 line-height, which these classes carry; the tag was never the
            contract. */}
        {children && <div className="text-caption text-secondary leading-[1.5]">{children}</div>}
      </div>
    </div>
  );
}
